import { FC, useMemo, useState } from "react";
import { AppThemeIconProperty, MasterModel } from "../Types";
import { Autocomplete, Box, Grid, IconButton, InputAdornment, makeStyles, TextField, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useAppThemeContext } from "src/contexts/AppThemeContext";
import { useConfig } from "src/hooks/useConfig";
import { useIcons } from "src/contexts/IconContext";
import FontAwesomeIconSelector, { FontAwesomeIconOption } from "src/components/ui-components/FontAwesomeIconSelector";
import ColorPickerWithAlpha from "src/components/ui-components/ColorPickerWithAlpha";

type IconPopoverProps = {
  property: AppThemeIconProperty;
};

const useStyles = makeStyles(() => ({
  inputRoot: {
    '& input': {
      paddingRight: '40px'
    }
  },
  adornmentBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginRight: '-8px',
  },
  arrowBtn: {
    padding: 0,
    height: '16px',
    width: '24px',
    minWidth: 0,
  },
}));

const IconPopover: FC<IconPopoverProps> = ({ property }) => {
  const { masterOptions, updateIconData } = useAppThemeContext();
  const { icons } = useIcons();
  const classes = useStyles();
  const sizes = useMemo(() => masterOptions.iconSizes.map(s => s.toString()), [masterOptions.iconSizes]);
  const currentIndex = sizes.indexOf(property.size?.toString());
  const { isAdminUser } = useConfig();
  const [state, setState] = useState({
    icon: property.icon,
    size: property.size,
    type: property.type,
    colour: property.colour,
    alpha: property.alpha ?? 1,
  });

  const maxIconSize = Math.max(
    ...masterOptions.iconSizes.map(option => Number(option))
  );

  const handleUpClick = () => {
    if (currentIndex < sizes.length - 1) {
      handleUpdate('size', sizes[currentIndex + 1]);
    }
  };
  const handleDownClick = () => {
    if (currentIndex > 0) {
      handleUpdate('size', sizes[currentIndex - 1]);
    }
  };

  const getIconDefinition = (
    iconValue: string,
    type: string | undefined
  ): IconDefinition | null => {
    if (!iconValue || !icons.length) {
      return null;
    }

    const getStylePrefix = (style: string): string => {
      const styleMap: Record<string, string> = {
        'solid': 'fas',
        'regular': 'far',
        'light': 'fal',
        'thin': 'fat',
        'duotone': 'fad'
      };
      return styleMap[style] || 'fas';
    };

    let matchedIcon = null;

    if (iconValue.startsWith("0x")) {
      const hex = iconValue.toLowerCase().replace(/^0x/, "");
      matchedIcon = icons.find(icon => {
        const matches = icon.unicode.toLowerCase() === hex && 
          (!type || icon.type === type || icon.style === type);
        return matches;
      });
    } else if (iconValue.includes(':')) {
      const [styleOrPrefix, iconName] = iconValue.split(':');
      matchedIcon = icons.find(icon => 
        icon.iconName === iconName && 
        (icon.style === styleOrPrefix || getStylePrefix(icon.style) === styleOrPrefix)
      );
    } else {
      matchedIcon = icons.find(icon => 
        icon.iconName === iconValue && 
        (!type || icon.type === type || icon.style === type)
      );
    }

    if (!matchedIcon) {
      return null;
    }

    const iconDefinition: IconDefinition = {
      prefix: getStylePrefix(matchedIcon.style) as any,
      iconName: matchedIcon.iconName as any,
      icon: [
        matchedIcon.viewBox[2],
        matchedIcon.viewBox[3],
        [],
        matchedIcon.unicode,
        Array.isArray(matchedIcon.svgPath) ? matchedIcon.svgPath : [matchedIcon.svgPath]
      ]
    };

    return iconDefinition;
  };

  const handleUpdate = async (
    jsonKey: string,
    value: string | number | { color: string; alpha: number } | FontAwesomeIconOption | MasterModel
  ) => {
    setState(prev => {
      let updated = { ...prev };

      if (jsonKey === 'icon') {
        const iconOption = value as FontAwesomeIconOption;
        updated.icon = `0x${iconOption.processedIcon.unicode}`;
        updated.type = iconOption.processedIcon.type;
      } else if (jsonKey === 'size') {
        updated.size = Number(value);
      } else if (jsonKey === 'colour') {
        if (typeof value === 'object') {
          updated.colour = (value as any).color;
          updated.alpha = Number(Number((value as any).alpha).toFixed(2));
        } else {
          updated.colour = value as string;
        }
      } else if (jsonKey === 'alpha') {
        updated.alpha = Number(Number(value).toFixed(2));
      }

      updateIconData(
        property.themeIconPropertyKey,
        updated.icon,
        updated.size,
        updated.colour,
        updated.type,
        updated.alpha
      );

      return updated;
    });
  };

  const iconDefinition = getIconDefinition(state.icon, property.type);

  return (
    <>
      <Typography sx={{ marginBottom: '2%' }}>{property.label}</Typography>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <ColorPickerWithAlpha
              propKey={property.themeIconPropertyKey}
              color={state.colour}
              alpha={state.alpha}
              handleChange={(key, value) => {
                handleUpdate('colour', value);
              }}
            />
          </Grid>

          <Grid container display='flex' flexDirection='row' spacing={2}>
            <Grid item xs={12} sm={12} md={8} lg={8} xl={8} display='flex' flexDirection='column'>
              <Box sx={{ marginBottom: '2%' }}>
                <FontAwesomeIconSelector
                  value={state.icon}
                  onChange={(newValue) => { handleUpdate('icon', newValue) }}
                  label="Icon"
                />
              </Box>
              <Box sx={{ marginTop: '2%' }}>
                <Autocomplete
                  freeSolo={isAdminUser}
                  disableClearable
                  popupIcon={null}
                  options={masterOptions.iconSizes}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option?.toString() ?? ''
                  }
                  value={sizes.find((x) => x.toString() === state.size?.toString()) || state.size?.toString() || ''}
                  onChange={(_, newValue, reason) => {
                    if (typeof newValue === 'string' || typeof newValue === 'number') {
                      const numValue = Number(newValue);
                      if (reason === 'select-option' || (!isNaN(numValue) && numValue <= maxIconSize)) {
                        handleUpdate('size', newValue);
                      }
                    }
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (isAdminUser && reason === 'input') {
                      const numValue = Number(newInputValue);
                      if (newInputValue === '' || (!isNaN(numValue) && numValue >= 1 && numValue <= maxIconSize)) {
                        handleUpdate('size', newInputValue);
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Size"
                      size="small"
                      fullWidth
                      inputProps={{
                        ...params.inputProps,
                        min: 1,
                        max: maxIconSize,
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                          const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab', 'Backspace', 'Delete'];

                          if (navigationKeys.includes(e.key)) {
                            if (params.inputProps?.onKeyDown) {
                              params.inputProps.onKeyDown(e);
                            }
                            return;
                          }

                          if (isAdminUser && e.currentTarget.value) {
                            const currentValue = Number(e.currentTarget.value);
                            const newChar = e.key;

                            if (!isNaN(Number(newChar)) && !isNaN(currentValue)) {
                              const potentialValue = Number(e.currentTarget.value + newChar);
                              if (potentialValue > maxIconSize) {
                                e.preventDefault();
                              }
                            }
                          }
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        classes: { root: classes.inputRoot },
                        endAdornment: (
                          <InputAdornment position="end" className={classes.adornmentBox}>
                            <IconButton className={classes.arrowBtn} onClick={handleUpClick} size="small">
                              <ArrowDropUp fontSize="small" />
                            </IconButton>
                            <IconButton className={classes.arrowBtn} onClick={handleDownClick} size="small">
                              <ArrowDropDown fontSize="small" />
                            </IconButton>
                            {params.InputProps.endAdornment}
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
              <Box
                sx={{
                  p: 1,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  backgroundColor: "#ffffff",
                  textAlign: "center",
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 60,
                    height: 60
                  }}
                >
                  {iconDefinition ? (
                    <FontAwesomeIcon
                      icon={iconDefinition}
                      style={{
                        fontSize: state.size,
                        color: state.colour,
                        opacity: state.alpha ?? 1,
                        ['--fa-secondary-opacity' as any]: 1,
                        ['--fa-primary-opacity' as any]: 1,
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      No Icon
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default IconPopover;
